<template>
  <NativeDrawer
    v-model="drawer"
    :before-close="handleDrawerClose"
    size="80%"
  >
    <template #header>
      <h4>团队信息</h4>
    </template>
    <template #default>
      <div class="team-content">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <NativeSkeleton :rows="5" animated />
        </div>

        <!-- 团队列表 -->
        <div v-else class="team-list">
          <!-- 搜索筛选组件 -->
          <BaseSearch
            :search-form="searchForm"
            :fields="searchFields"
            @search="handleSearch"
            @reset="handleReset"
          />

          <!-- 表格组件 -->
          <TeamTable
            :team-list="teamList"
            @edit="handleEdit"
            @delete="handleDeleteConfirm"
          />

          <!-- 分页组件 -->
          <BasePagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>

        <!-- 编辑表单组件 -->
        <TeamForm
          v-model:visible="editDialogVisible"
          :is-edit="isEdit"
          :form-data="formData"
          :submit-loading="submitLoading"
          @submit="handleSubmitForm"
          @cancel="handleFormCancel"
        />
      </div>
    </template>
    <template #footer>
      <div
        style="flex: auto; display: flex; justify-content: flex-end; gap: 10px"
      >
        <NativeButton @click="addClick" type="success">新增团队信息</NativeButton>
        <NativeButton type="primary" @click="confirmClick">关闭</NativeButton>
      </div>
    </template>
  </NativeDrawer>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";
import { useTeamsStore, type TeamType } from "@/stores/teams";
import { notify } from "@/utils/nativeFeedback";

import type { SearchField } from "@/components/NativeUI/types";

// 导入子组件
import BaseSearch from "./BaseSearch/index.vue";
import TeamTable from "./team/TeamTable.vue";
import BasePagination from "./BasePagination/index.vue";
import TeamForm from "./team/TeamForm.vue";

// ==================== Props & Emits ====================
const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
}>();
const teamsStore = useTeamsStore();

// ==================== Drawer 状态 ====================
const drawer = ref(props.visible);

watch(
  () => props.visible,
  (newVal) => {
    drawer.value = newVal;
    if (newVal) {
      loadTeamList();
    }
  },
);

watch(drawer, (newVal) => {
  emit("update:visible", newVal);
});

// ==================== 数据相关 ====================
const loading = ref(false);
const allTeamList = ref<TeamType[]>([]);
const teamList = ref<TeamType[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 搜索表单
const searchForm = ref({
  name: "",
});

// 计算团队选项（去重并转换为对象数组格式）
const teamOptions = computed(() => {
  const uniqueNames = new Set<string>();
  allTeamList.value.forEach((item) => {
    if (item.name) {
      uniqueNames.add(item.name);
    }
  });
  return Array.from(uniqueNames).map((name) => ({ label: name, value: name }));
});

// 搜索字段配置
const searchFields = computed<SearchField[]>(() => [
  {
    label: "团队名称",
    prop: "name",
    type: "select",
    placeholder: "请选择团队名称",
    width: "150px",
    options: teamOptions.value,
  },
]);

// 筛选后的数据
const filteredList = computed(() => {
  let list = allTeamList.value;

  if (searchForm.value.name) {
    list = list.filter((item) => item.name?.includes(searchForm.value.name));
  }

  return list;
});

// 编辑表单
const editDialogVisible = ref(false);
const isEdit = ref(false);
const submitLoading = ref(false);
const formData = ref<Partial<TeamType>>({
  id: 0,
});

// ==================== API 调用 ====================
const updatePageData = () => {
  const list = filteredList.value;
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  teamList.value = list.slice(start, end);
  total.value = list.length;
};

const loadTeamList = async () => {
  loading.value = true;
  try {
    await teamsStore.refreshTeams();
    allTeamList.value = teamsStore.getTeamsList;
    updatePageData();
  } catch (error) {
    console.error("加载团队列表失败:", error);
    notify("加载团队列表失败", "error");
  } finally {
    loading.value = false;
  }
};

// ==================== 事件处理 ====================
const handleSearch = () => {
  currentPage.value = 1;
  updatePageData();
};

const handleReset = () => {
  searchForm.value = {
    name: "",
  };
  currentPage.value = 1;
  updatePageData();
};

const handleSizeChange = (val: number) => {
  pageSize.value = val;
  currentPage.value = 1;
  updatePageData();
};

const handleCurrentChange = (val: number) => {
  currentPage.value = val;
  updatePageData();
};

const addClick = () => {
  isEdit.value = false;
  formData.value = {
    id: 0,
    name: "",
    description: "",
    advantages: [],
    photographs: [],
  };
  editDialogVisible.value = true;
};

const handleEdit = (row: TeamType) => {
  isEdit.value = true;
  formData.value = {
    id: row.id,
    name: row.name || "",
    description: row.description || "",
    advantages: row.advantages || [],
    photographs: row.photographs || [],
  };
  editDialogVisible.value = true;
};

const handleDeleteConfirm = async (row: TeamType) => {
  if (!window.confirm(`确认删除成员 "${row.name}" 吗?`)) return;

  if (await teamsStore.deleteTeam(row.id)) {
    allTeamList.value = teamsStore.getTeamsList;
    updatePageData();
  }
};

const handleSubmitForm = async (data: Partial<TeamType>) => {
  submitLoading.value = true;
  try {
    const success =
      isEdit.value && data.id
        ? await teamsStore.updateTeamById(data.id, data)
        : await teamsStore.createTeam(data);

    if (success) {
      allTeamList.value = teamsStore.getTeamsList;
      updatePageData();
      editDialogVisible.value = false;
    }
  } finally {
    submitLoading.value = false;
  }
};

const handleFormCancel = () => {
  formData.value = {
    id: 0,
    name: "",
    description: "",
    photographs: [],
    advantages: [],
  };
  isEdit.value = false;
};

const handleDrawerClose = (done: () => void) => {
  if (window.confirm("确认是否关闭?")) done();
};

const confirmClick = () => {
  drawer.value = false;
};

// 监听筛选结果变化
watch(filteredList, () => {
  currentPage.value = 1;
  updatePageData();
});
</script>

<style scoped lang="scss">
.team-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;

  .loading-container {
    padding: 20px;
  }

  .team-list {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    // 搜索筛选区域 - 固定在顶部
    :deep(.search-filter) {
      flex-shrink: 0;
    }

    // 表格容器 - 占据剩余空间并可滚动
    :deep(.team-table) {
      flex: 1;
      min-height: 0;
      overflow: auto;
    }

    // 分页组件 - 固定在底部
    :deep(.pagination-container) {
      flex-shrink: 0;
      border-top: 1px solid #ebeef5;
      background-color: #fff;
    }
  }
}
</style>
